import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { textsPath } from '../utils/textsLink';
import CustomerIntake from '../components/CustomerIntake';
import { UserPlus, Check, Clock, AlertCircle, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const STATUS_OPTIONS = ['new', 'contacted', 'quoted', 'converted', 'lost'];
const STATUS_BADGE: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  quoted: 'bg-brand-100 text-brand-700',
  converted: 'bg-green-100 text-green-700',
  lost: 'bg-gray-100 text-gray-500',
};

const fmtDate = (d?: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function Leads() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState<any | null>(null);

  const { data: statsData } = useQuery('lead-stats', () => api.get('/leads/stats').then(r => r.data));
  const { data, isLoading } = useQuery(
    ['leads', statusFilter],
    () => api.get('/leads', { params: statusFilter ? { status: statusFilter } : undefined }).then(r => r.data.leads as any[])
  );
  const leads = data || [];

  const updateStatus = useMutation(async ({ id, status }: { id: number; status: string }) => {
    await api.put(`/leads/${id}`, { status });
  }, {
    onSuccess: () => { qc.invalidateQueries('leads'); toast.success('Status updated'); },
    onError: () => { toast.error('Failed to update'); },
  });

  // The same mowing quote the website path drafts on its own — on demand,
  // for a lead that came in by phone or before auto-quote was on.
  const draftQuote = useMutation((id: number) => api.post(`/leads/${id}/auto-quote`).then(r => r.data), {
    onSuccess: (r: any) => { qc.invalidateQueries('leads'); toast.success(r?.message || 'Quote drafted'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not draft that quote'); },
  });

  function openConvertForm(lead: any) {
    // Navigate to Customers page with lead data pre-filled in the New Customer modal
    navigate('/customers', {
      state: {
        prefillLead: {
          lead_id:  lead.id,
          name:     lead.name     || lead.company_name || '',
          phone:    lead.phone    || '',
          email:    lead.email    || '',
          address:  lead.address  || '',
          city:     lead.city     || '',
          how_heard: lead.how_heard || '',
          wants:    lead.wants    || (lead.requested_service === 'mowing' ? 'mowing' : lead.requested_service === 'treatments' ? 'plan' : lead.requested_service ? 'project' : ''),
          notes:    lead.message  || '',
          source:   'lead',
        }
      }
    });
  }

  const scanTexts = useMutation(async () => {
    const r = await api.post('/leads/scan-texts', { hours: 24 });
    return r.data;
  }, {
    onSuccess: (data: any) => {
      qc.invalidateQueries('leads');
      qc.invalidateQueries('attention');
      if (data.action_items > 0) {
        toast.info(`${data.action_items} item${data.action_items > 1 ? 's' : ''} need attention`, { autoClose: 6000 });
      }
      if (data.checked > 0) {
        toast.success(`${data.checked} conversation${data.checked > 1 ? 's' : ''} checked — all good ✓`, { autoClose: 5000 });
      }
      toast.success(data.summary || 'Scan complete');
      if (data.text_error) toast.warn(data.text_error, { autoClose: 8000 });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Scan failed — check console for details');
    },
  });

  const newCount = leads.filter(l => l.status === 'new').length;

  // A lead typed in by hand — someone met on the street, a call, a referral.
  const [newLead, setNewLead] = useState(false);

  return (
    <div className="space-y-6">
      {/* On a phone the buttons drop under the title instead of squeezing it into a column. */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Leads</h1>
          <p className="text-sm text-gray-500 mt-0.5">New potential customers · Follow-ups land in <a href="/attention" className="text-brand-600 underline">Needs Attention</a></p>
        </div>
        <div className="flex items-center gap-2">
          {newCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-800 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg">
              <AlertCircle className="h-4 w-4" />{newCount} new
            </span>
          )}
          <button onClick={() => setNewLead(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 bg-white border border-brand-300 rounded-lg hover:bg-brand-50">
            <UserPlus className="h-4 w-4" /> New lead
          </button>
          <button
            onClick={() => scanTexts.mutate()}
            disabled={scanTexts.isLoading}
            className="press inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-60 transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            {scanTexts.isLoading ? 'Scanning…' : 'Scan Texts'}
          </button>
        </div>
      </div>

      {newLead && (
        <div className="card p-4">
          <CustomerIntake mode="lead" source="manual" title="New lead" onCancel={() => setNewLead(null)}
            onSaved={() => { qc.invalidateQueries('leads'); qc.invalidateQueries('lead-stats'); setNewLead(null); toast.success('Lead added'); }} />
        </div>
      )}

      {/* Conversion stats */}
      {statsData && statsData.total > 0 && (
        <div className="card p-4">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Conversion rate</p>
              <p className="text-2xl font-bold text-green-700">{statsData.conversion_rate}%</p>
              <p className="text-[11px] text-gray-500">{statsData.converted} of {statsData.total} leads · {statsData.last_30_days?.conversion_rate}% last 30 days</p>
            </div>
            <div className="flex-1 min-w-[16rem]">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Funnel (current status)</p>
              <div className="flex flex-wrap gap-1.5">
                {(['new', 'contacted', 'quoted', 'converted', 'lost'] as const).map(s => (
                  <span key={s} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                    {s}: <b>{statsData.by_status?.[s] || 0}</b>
                  </span>
                ))}
              </div>
            </div>
          </div>
          {statsData.by_source?.length > 0 && (
            <div className="mt-3 border-t border-gray-100 pt-2">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">By source</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                {statsData.by_source.map((s: any) => (
                  <span key={s.source}>{s.source}: <b className="text-gray-800">{s.rate}%</b> <span className="text-gray-500">({s.converted}/{s.total})</span></span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* The website form is already wired to this; the address is here for whoever edits the site, folded away. */}
      <details className="card p-4">
        <summary className="text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer">For whoever edits the website</summary>
        <p className="text-xs text-gray-500 mt-2 mb-1">The website form posts to this address (5 requests per hour from one place):</p>
        <code className="block text-xs bg-gray-50 rounded px-3 py-2 font-mono text-gray-700 break-all">
          {(process.env.REACT_APP_API_URL || '').replace(/\/api$/, '')}/api/leads/public
        </code>
        <p className="text-xs text-gray-500 mt-1">Fields: name, email, phone, company_name, address, city, requested_service, message, preferred_contact</p>
      </details>

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {['', ...STATUS_OPTIONS].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors ${
              statusFilter === s ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-600 border-gray-300 hover:border-brand-400'
            }`}>
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center text-gray-500 py-8">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">
          <UserPlus className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p>No leads yet.</p>
          <p className="text-sm mt-1">Add the API endpoint to your website form to start receiving inquiries.</p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {leads.map((lead: any) => (
            <div key={lead.id}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900">{lead.name || lead.company_name || '—'}</p>
                    {lead.company_name && lead.name && (
                      <span className="text-xs text-gray-500">{lead.company_name}</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[lead.status] || 'bg-gray-100 text-gray-500'}`}>
                      {lead.status}
                    </span>
                    {lead.source === 'text_message' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-medium">
                        📱 Text
                      </span>
                    )}
                    {(lead.preferred_day || lead.preferred_window) && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-medium capitalize" title="Preferred day and visit window from the quote form">
                        🕘 {[lead.preferred_day, lead.preferred_window].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {[lead.email, lead.phone, lead.city].filter(Boolean).join(' · ')}
                    {lead.requested_service ? ` · ${lead.requested_service}` : ''}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-gray-500"><Clock className="inline h-3 w-3 mr-0.5" />{fmtDate(lead.created_at)}</p>
                  {lead.converted_customer_id && (
                    <p className="text-xs text-green-600 mt-0.5 flex items-center justify-end gap-0.5">
                      <Check className="h-3 w-3" />Customer
                    </p>
                  )}
                </div>
              </div>

              {/* Expanded detail */}
              {selectedLead?.id === lead.id && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                  {lead.message && (
                    <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 italic">
                      "{lead.message}"
                    </div>
                  )}
                  {lead.address && (
                    <p className="text-xs text-gray-500">
                      Address: {[lead.address, lead.city].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Preferred contact: {lead.preferred_contact || 'email'} · Source: {lead.source || 'website_form'}
                  </p>

                  {/* Status change */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-500">Move to:</span>
                    {STATUS_OPTIONS.filter(s => s !== lead.status).map(s => (
                      <button key={s} onClick={e => { e.stopPropagation(); updateStatus.mutate({ id: lead.id, status: s }); }}
                        disabled={updateStatus.isLoading}
                        className="px-2.5 py-1 text-xs rounded-full border border-gray-300 text-gray-600 hover:border-brand-400 hover:text-brand-700 transition-colors disabled:opacity-50">
                        {s}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Their thread on Texts — the reply goes from the business number, never from his phone */}
                    {lead.phone && (
                      <Link
                        to={textsPath(lead.phone)}
                        onClick={e => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100">
                        💬 Text back
                      </Link>
                    )}
                    {/* Mowing lead with an address: the app prices it and drafts the bid */}
                    {lead.address && !lead.quote_id && (
                      <button
                        onClick={e => { e.stopPropagation(); draftQuote.mutate(lead.id); }}
                        disabled={draftQuote.isLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 disabled:opacity-50">
                        {draftQuote.isLoading ? 'Drafting…' : '🧾 Draft mowing quote'}
                      </button>
                    )}
                    {lead.quote_id && (
                      <button
                        onClick={e => { e.stopPropagation(); navigate('/quotes', { state: { openQuoteId: lead.quote_id } }); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100">
                        Open bid #{lead.quote_id}
                      </button>
                    )}
                    {/* Convert to customer — opens pre-filled form */}
                    {!lead.converted_customer_id && (
                      <button
                        onClick={e => { e.stopPropagation(); openConvertForm(lead); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                        <UserPlus className="h-4 w-4" /> Convert to Customer
                      </button>
                    )}
                  </div>
                  {lead.converted_customer_id && (
                    <p className="text-sm text-green-700 font-medium flex items-center gap-1">
                      <Check className="h-4 w-4" /> Converted → {lead.converted_customer_name || `Customer #${lead.converted_customer_id}`}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
